import { useCallback, useEffect, useState } from 'react';
import { ShieldAlertIcon } from 'lucide-react';
import { app, comments, task, type CommentBackupSnapshot } from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { AccountCard } from '@/components/AccountCard';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/skeletons';
import { InfoCard, TaskProgressCard } from '@/components/task-ui';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CARD, HINT, MUTED, NUM } from '@/components/ui';
import { cn } from '@/lib/utils';

/**
 * 薯薯全能通 · 评论备份（`/shushu/comment-backup?accountId=`）。
 *
 * 对照搬小书 `mobile/tools/comment-backup`：把评论**存下来**（一级评论，暂不含楼中楼），重复备份按笔记覆盖。
 * 与搬小书不同，循环在原生任务引擎（`comments.start`）：对本机已备份的笔记逐篇读全评论，可暂停续跑、任务中心可见；
 * 存下的评论在笔记详情里看，导出笔记时一起带上。
 */

const MIN_BRIDGE = 6;

export function ShushuCommentBackupPage() {
  const { info, loading: infoLoading, isNative } = useAppInfo();
  const { accounts, selectedId: accountId, loading: accountsLoading, error: accountError } = useAccounts();
  const [snapshot, setSnapshot] = useState<CommentBackupSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  useGroupedSurface();

  const account = accounts.find((a) => a.id === accountId) ?? null;
  const outdated = !!info && info.bridgeVersion < MIN_BRIDGE;
  const supported = !!account?.capabilities?.includes('commentsBackup');
  const loaded = !infoLoading && !accountsLoading && (snapshot !== null || outdated || !accountId || error !== null);
  useAppReady(loaded);

  useEffect(() => {
    if (!accountId || outdated || infoLoading) return;
    let cancelled = false;
    comments
      .state(accountId)
      .then((state) => {
        if (!cancelled) setSnapshot(state);
      })
      .catch((e) => {
        if (!cancelled) setError(describeError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, outdated, infoLoading, tick]);

  useEffect(
    () =>
      comments.onProgress((next) => {
        if (next.accountId !== accountId) return;
        setSnapshot((prev) => ({ ...next, stats: prev?.stats }));
        if (next.state === 'running') return;
        void app.haptic(next.state === 'completed' && !next.failed ? 'success' : 'error');
        setTick((t) => t + 1);
      }),
    [accountId],
  );

  const running = snapshot?.state === 'running';
  const stopped = snapshot?.state === 'failed' || snapshot?.state === 'paused';
  const finished = snapshot?.state === 'completed';
  const stats = snapshot?.stats;

  const start = useCallback(async () => {
    if (!accountId) return;
    setBusy(true);
    setError(null);
    try {
      const next = await comments.start(accountId);
      setSnapshot((prev) => ({ ...next, stats: prev?.stats }));
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }, [accountId]);

  const control = useCallback(
    async (kind: 'pause' | 'resume') => {
      if (!snapshot?.taskId) return;
      setBusy(true);
      setError(null);
      try {
        await (kind === 'pause' ? task.pause(snapshot.taskId) : task.resume(snapshot.taskId));
        if (kind === 'resume') setSnapshot({ ...snapshot, state: 'running', error: undefined });
      } catch (e) {
        setError(describeError(e));
      } finally {
        setBusy(false);
      }
    },
    [snapshot],
  );

  const done = snapshot?.done ?? 0;
  const failed = snapshot?.failed ?? 0;
  const total = Math.max(snapshot?.total ?? 0, done + failed);

  return (
    <PageShell title="评论备份" description="把笔记下的评论存到本机">
      {!loaded ? <PageSkeleton kind="commentBackup" /> : null}

      {loaded && !isNative ? (
        <InfoCard title="请在 App 内打开">浏览器里单独调试请加 <code>?mock=1</code>。</InfoCard>
      ) : null}
      {outdated ? (
        <InfoCard title="需要更新 App" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          当前 App 版本还不支持评论备份，请更新后再试。
        </InfoCard>
      ) : null}

      {loaded && account && !outdated ? (
        <>
          <InfoCard>
            备份的是<strong className="text-foreground font-semibold">一级评论</strong>
            （暂不含楼中楼），存到本机；重复备份会覆盖同一篇笔记的旧数据。
          </InfoCard>
          <AccountCard account={account} showId={false} />
        </>
      ) : null}

      {loaded && account && !outdated && !supported ? (
        <InfoCard title="暂不支持" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          这个平台还不支持备份评论。
        </InfoCard>
      ) : null}

      {snapshot && (running || stopped || finished) ? (
        <TaskProgressCard
          title={running ? '正在备份评论' : stopped ? '已暂停' : '本次结果'}
          right={`${done + failed} / ${total} 篇`}
          done={done + failed}
          total={total}
          current={running ? snapshot.current : undefined}
          finished={finished}
          paused={stopped}
          bar={!finished}
          action={
            <>
              <div className={cn('text-muted-foreground flex gap-3.5 text-footnote', NUM)}>
                <span>已存 {snapshot.comments ?? 0} 条评论</span>
                <span className={cn(failed > 0 && 'text-destructive')}>失败 {failed} 篇</span>
              </div>
              <p className={cn(HINT, stopped && 'text-destructive')}>
                {running
                  ? '翻页间隔约 1.5 秒，可以离开本页，进度在任务中心也能看到。'
                  : stopped
                    ? `${snapshot.error ? describeError(snapshot.error) : '已暂停'}。继续后从没备份的那篇接着来。`
                    : `已完成，备份了 ${done} 篇笔记的 ${snapshot.comments ?? 0} 条评论${failed ? `，${failed} 篇失败（任务中心可看原因）` : ''}。`}
              </p>
              {running ? (
                <Button variant="tinted" className="w-full" disabled={busy} onClick={() => void control('pause')}>
                  暂停
                </Button>
              ) : stopped ? (
                <Button className="w-full" disabled={busy} onClick={() => void control('resume')}>
                  继续
                </Button>
              ) : null}
            </>
          }
        />
      ) : null}

      {loaded && account && supported && !running && !stopped ? (
        <>
          <Card className={CARD}>
            <p className="text-callout font-semibold">备份全部笔记的评论</p>
            <p className={MUTED}>
              {stats?.targets === 0
                ? '本机还没有已备份、且有评论的笔记。评论备份只处理已经备份到本机的笔记，先去「数据备份」备份笔记。'
                : stats
                  ? `本机已备份的笔记里有 ${stats.targets} 篇可以备份评论${stats.notes ? `；已存过 ${stats.notes} 篇、共 ${stats.comments} 条评论` : ''}。`
                  : '对本机已备份的笔记逐篇读取评论。'}
            </p>
            <p className={HINT}>存下的评论在笔记详情里查看，导出笔记时会一起带上。</p>
          </Card>
          {stats?.targets === 0 ? (
            <Button
              variant="tinted"
              className="w-full"
              onClick={() => void app.openPage(`/shushu/backup?accountId=${encodeURIComponent(account.id)}`, '数据备份')}
            >
              去数据备份
            </Button>
          ) : (
            <Button
              className="disabled:bg-primary/35 disabled:text-primary-foreground w-full"
              disabled={busy || !account.canWrite}
              onClick={() => void start()}
            >
              {finished ? '重新备份评论' : '开始备份评论'}
            </Button>
          )}
        </>
      ) : null}

      {account && !account.canWrite && loaded ? (
        <p className={cn('mx-1', HINT, 'text-destructive')}>登录已失效，重新登录后才能备份评论。</p>
      ) : null}
      {(error ?? accountError) ? <p className="text-destructive px-1 text-footnote">{error ?? accountError}</p> : null}
    </PageShell>
  );
}
